import axios, { AxiosError, CanceledError, InternalAxiosRequestConfig } from 'axios';
import {
  beginOperatingContext, getOperatingContext, isOperatingContextReady,
} from '../utils/operatingContextLifecycle';
import { emitToast } from '../utils/toastEvents';
import { createIdempotencyKey } from '../utils/operationKey';
import { getOperatingContextErrorToast } from '../utils/operatingContextErrors';
export { OPERATING_GROUP_STORAGE_KEY, OPERATING_GROUP_CHANGED_EVENT } from '../utils/operatingContextLifecycle';

export const resolveApiBase = (apiBase = process.env.REACT_APP_API_URL) => apiBase?.trim() || '/api';

export const resolveApiUrl = (path = '') => {
  const cleanPath = `${path ?? ''}`.trim();
  if (/^https?:\/\//i.test(cleanPath)) {
    return cleanPath.replace(/\/+$/, '');
  }

  const base = resolveApiBase().replace(/\/+$/, '');
  const normalizedPath = cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath;

  if (!normalizedPath) {
    return base;
  }

  return `${base}/${normalizedPath}`;
};

const requestContexts = new WeakMap<InternalAxiosRequestConfig, { token: string; generation: number }>();
export const getActiveOperatingGroupId = () => {
  const context = getOperatingContext();
  return isOperatingContextReady(context, localStorage.getItem('token')) ? context.groupId : null;
};
const normalizePath = (url = '') => url.split('?', 1)[0]
  .replace(/^https?:\/\/[^/]+/i, '').replace(/^\/?api\//i, '').replace(/^\/+|\/+$/g, '').toLowerCase();

export const isPublicApiRequest = (method = 'get', url = '') => {
  const path = normalizePath(url);
  const verb = method.toLowerCase();
  return (verb === 'post' && /^auth\/(login|register)$/.test(path))
    || (verb === 'get' && (path === 'permissions/ping' || /^qr\/scan\/[^/]+$/.test(path)
      || /^(menu(\/\d+)?|sale-catalog(\/dishes\/\d+\/photo)?)$/.test(path)
      || /^event-venue-bookings\/(venues|availability|\d+\/menu(\/dishes\/\d+\/photo)?)$/.test(path)))
    || (verb === 'post' && /^(event-venue-bookings|guests\/public|orders)$/.test(path))
    || (verb === 'put' && /^(orders\/\d+\/tips|event-venue-bookings\/\d+\/menu)$/.test(path))
    || (['get', 'put'].includes(verb) && /^venue-layouts\/customer\/bookings\/\d+$/.test(path));
};

const isOperatingGroupAdministrationRequest = (method = 'get', path = '') => {
  const verb = method.toLowerCase();
  return ((verb === 'get' || verb === 'put') && /^operating-groups\/\d+\/sale-catalog$/.test(path))
    || (verb === 'post' && (
      path === 'operating-groups'
      || /^operating-groups\/\d+\/(archive|departments|warehouses|configuration)$/.test(path)
      || path === 'operating-groups/warehouses'
    ))
    || (verb === 'put' && (
      /^operating-groups\/\d+$/.test(path)
      || /^operating-groups\/warehouses\/\d+$/.test(path)
    ));
};

const isContextAdministrationRequest = (method = 'get', url = '') => {
  const path = normalizePath(url);
  return /^operating-contexts(\/(selection|endpoint-scopes|administration|mode))?$/.test(path)
    || /^operating-contexts\/(restaurant-access|users|ownership-reconciliation)(\/|$)/.test(path)
    || /^notifications\/settings(\/|$)/.test(path)
    || isOperatingGroupAdministrationRequest(method, path);
};

const staleRequest = (config?: InternalAxiosRequestConfig) => {
  const captured = config && requestContexts.get(config);
  return captured && (captured.token !== localStorage.getItem('token')
    || captured.generation !== getOperatingContext().generation);
};

const idempotentPostPaths = new Set([
  'inventoryreceipts',
  'inventoryreceipts/import-batch-create',
  'inventorywriteoffs',
  'inventoryadjustments/transfer',
  'inventoryadjustments/write-off',
  'inventoryadjustments/nutrition',
  'inventoryadjustments/resort',
  'inventoryrevisions',
  'purchaseworksheets/generate',
  'eventorders',
  'eventorders/excel/import',
  'supplierpaymentbatches',
  'suppliers/banking-import-apply',
  'restaurant-personnel',
  'restaurant-personnel/departments',
  'semifinishedproductiontasks',
  'semifinishedproductions',
]);

export const requiresIdempotencyKey = (method = '', url = '') => {
  const normalizedMethod = method.toLowerCase();
  const path = url
    .split('?', 1)[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/?api\//i, '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
  if (normalizedMethod === 'put') {
    return /^semifinishedproductiontasks\/\d+\/status$/.test(path);
  }
  if (normalizedMethod !== 'post') return false;
  return idempotentPostPaths.has(path)
    || path.startsWith('hotel/')
    || /^inventoryreceipts\/\d+\/payments$/.test(path)
    || /^supplierpaymentbatches\/\d+\/execute$/.test(path)
    || /^halls-payroll\/\d+\/\d+\/adjustments$/.test(path)
    || /^restaurant-payroll\/\d+\/\d+\/adjustments$/.test(path)
    || /^restaurant-personnel\/\d+\/(medical-checks|issued-items)$/.test(path)
    || /^restaurant-personnel\/\d+\/issued-items\/\d+\/return$/.test(path)
    || /^employee-dossiers\/[^/]+\/(medical-checks|issued-items)$/.test(path);
};


const api = axios.create({
  baseURL: resolveApiBase(),
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const publicRequest = isPublicApiRequest(config.method, config.url);
  config.headers.delete('X-Operating-Group-Id');
  config.headers.delete('Authorization');
  if (token && !publicRequest) {
    const context = getOperatingContext();
    const administration = isContextAdministrationRequest(config.method, config.url);
    if (!administration && !isOperatingContextReady(context, token)) {
      throw new AxiosError('Операційний контекст не підтверджено. Оберіть групу або повторіть завантаження контексту.',
        'ERR_OPERATING_CONTEXT_NOT_READY', config);
    }
    if (context.token === token && context.status === 'all' && normalizePath(config.url) !== 'operating-contexts/selection'
      && !['get', 'head', 'options'].includes(config.method || 'get')) {
      throw new AxiosError('Огляд усіх груп доступний лише для читання.', 'ERR_OPERATING_CONTEXT_READ_ONLY', config);
    }
    config.headers.Authorization = 'Bearer ' + token;
    if (!administration && context.groupId) config.headers['X-Operating-Group-Id'] = context.groupId;
    requestContexts.set(config, { token, generation: context.generation });
  }
  if (requiresIdempotencyKey(config.method, config.url) && !config.headers['Idempotency-Key']) {
    config.headers['Idempotency-Key'] = createIdempotencyKey();
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    if (staleRequest(response.config)) throw new CanceledError('Попередній операційний контекст скасовано.', response.config);
    return response;
  },
  (error) => {
    if (staleRequest(error?.config)) return Promise.reject(new CanceledError('Попередній операційний контекст скасовано.', error.config));
    const status = error?.response?.status;
    const requestUrl: string = error?.config?.url || '';
    const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');

    if (status === 401 && !isAuthRequest && !isPublicApiRequest(error?.config?.method, requestUrl)) {
      beginOperatingContext(null);
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('roles');
      localStorage.removeItem('permissions');

      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }

    const operatingContextToast = getOperatingContextErrorToast(error);
    if (operatingContextToast && !axios.isCancel(error)) {
      emitToast({ kind: 'error', ...operatingContextToast });
    } else if (!axios.isCancel(error) && (!status || status === 409 || status >= 500)) {
      const responseData = error?.response?.data;
      const details = responseData?.message
        || responseData?.title
        || (Array.isArray(responseData?.errors) ? responseData.errors.join(' ') : null)
        || error?.message
        || 'Спробуйте ще раз або зверніться до адміністратора.';
      emitToast({
        kind: 'error',
        title: status === 409
          ? 'Конфлікт змін'
          : status ? 'Помилка сервера' : 'Немає зв’язку із сервером',
        message: String(details),
      });
    }

    return Promise.reject(error);
  }
);

export default api;
